'use client';

import { isCancelledError } from '@tanstack/react-query';
import { isCancel } from 'axios';
const lookupCancelled = (error: unknown) =>
  isCancelledError(error) || isCancel(error) || (error instanceof DOMException && error.name === 'AbortError');

import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';

import { cn } from '@shared/utils/utils';
import { faCheck, faChevronDown, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@shared/stores/authStore';

// 🎯 Tipos para el componente
export interface DynamicComboboxOption {
  label: string;
  value: string;
}

export interface DynamicComboboxProps {
  ariaLabel?: string;
  searchPlaceholder?: string;
  loadingMoreMessage?: string;
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  /** append=true: siguiente página (scroll infinito); el padre puede ignorar el segundo argumento. */
  searchFn: (query: string, append?: boolean) => Promise<DynamicComboboxOption[]>;
  className?: string;
  disabled?: boolean;
  dataTestId?: string;
  debounceMs?: number;
  pageSize?: number;
  emptyMessage?: string;
  loadingMessage?: string;
  // 🆕 Callback cuando se selecciona una opción (para propagar label)
  onOptionSelected?: (option: DynamicComboboxOption | null) => void;
  // 🆕 Callback para scroll infinito (se llama cuando el usuario llega al final)
  onLoadMore?: () => Promise<void>;
  // 🆕 Si hay más páginas disponibles para cargar
  hasMore?: boolean;
  /** Si el valor inicial no está en la primera página de búsqueda, resolver etiqueta por id */
  resolveSelectedOption?: (value: string) => Promise<DynamicComboboxOption | null>;
}

// 🎯 Hook para debounce personalizado
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const DynamicCombobox = ({
  ariaLabel,
  searchPlaceholder = 'Buscar...',
  loadingMoreMessage = 'Cargando más...',
  value,
  setValue,
  placeholder,
  searchFn,
  className = 'w-full',
  disabled = false,
  dataTestId,
  debounceMs = 100,
  emptyMessage = 'No se encontraron resultados',
  loadingMessage = 'Filtrando...',
  onOptionSelected,
  onLoadMore,
  hasMore = false,
  resolveSelectedOption,
}: DynamicComboboxProps) => {
  const { t } = useTranslation('ui');
  const scope = useAuthStore((state) => state.getAuthorizationScope());
  const failedSearch = useRef<string | null>(null);
  const failedLabel = useRef<string | null>(null);
  const [lookupError, setLookupError] = useState(false);
  const [failedAppend, setFailedAppend] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<DynamicComboboxOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 🎯 Estado separado para mantener la opción seleccionada
  const [selectedOption, setSelectedOption] = useState<DynamicComboboxOption | null>(null);

  // Referencias para la virtualización
  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<DynamicComboboxOption[]>([]);
  const valueRef = useRef(value);
  const labelRequestRef = useRef(0);
  const pendingLabelRef = useRef<string | null>(null);
  // Solo la búsqueda de esta generación puede escribir opciones o apagar la carga.
  const requestGenerationRef = useRef(0);
  optionsRef.current = options;
  valueRef.current = value;

  // Debounce del query de búsqueda
  const debouncedSearchQuery = useDebounce(searchQuery, debounceMs);

  // 🎯 Configuración del virtualizador
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40, // Altura estimada de cada item
    overscan: 5, // Items adicionales a renderizar fuera del viewport
  });

  // 🎯 Función de búsqueda memoizada (simplificada para filtrado local)
  // ⚠️ NO incluir options en deps: causaría bucle infinito (performSearch -> setOptions -> options cambia -> performSearch se recrea -> useEffect vuelve a ejecutar)
  const performSearch = useCallback(
    async (query: string, append = false) => {
      const generation = append ? requestGenerationRef.current : ++requestGenerationRef.current;
      try {
        setLookupError(false);
        if (!append) {
          setIsLoading(true);
          // La página que seguía cargando pertenece a la búsqueda anterior.
          setIsLoadingMore(false);
        }
        const results = await searchFn(query, append);
        if (generation !== requestGenerationRef.current) {
          return;
        }

        if (append) {
          // 🎯 Agregar resultados a los existentes (scroll infinito)
          setOptions((prev) => {
            const existingValues = new Set(prev.map((opt) => opt.value));
            const newOptions = results.filter((opt) => !existingValues.has(opt.value));
            return [...prev, ...newOptions];
          });
        } else {
          // 🎯 Reemplazar resultados (nueva búsqueda)
          setOptions(results);
        }

        // Solo pinta la etiqueta si el resultado sigue siendo el valor vigente.
        const currentValue = valueRef.current;
        if (currentValue) {
          const allOptions = append ? [...optionsRef.current, ...results] : results;
          const foundOption = allOptions.find((option) => option.value === currentValue);
          if (foundOption) {
            setSelectedOption((current) => (current?.value === currentValue ? current : foundOption));
          }
        }
      } catch (err) {
        if (generation !== requestGenerationRef.current || lookupCancelled(err)) return;
        setLookupError(true);
        failedSearch.current = `${scope}:${query}`;
        setFailedAppend(append);
        if (!append) {
          setOptions([]);
        }
      } finally {
        if (generation === requestGenerationRef.current) {
          setIsLoading(false);
        }
      }
    },
    [searchFn, scope],
  );

  // Resuelve la etiqueta del valor vigente e ignora el resultado si ese valor ya cambió.
  const performInitialSearch = useCallback(
    async (searchValue: string, requestId: number) => {
      const stillCurrent = () => requestId === labelRequestRef.current && valueRef.current === searchValue;

      try {
        const results = await searchFn('', false);
        if (!stillCurrent()) return;
        const foundOption = results.find((option) => option.value === searchValue);
        if (foundOption) {
          setSelectedOption(foundOption);
          pendingLabelRef.current = null;
          return;
        }
        const resolved = await resolveSelectedOption?.(searchValue);
        if (!stillCurrent()) return;
        if (resolved?.value === searchValue) {
          setSelectedOption(resolved);
        }
      } catch (err) {
        if (!stillCurrent() || lookupCancelled(err)) return;
        setLookupError(true);
        failedLabel.current = `${scope}:${searchValue}`;
      } finally {
        if (pendingLabelRef.current === searchValue && requestId === labelRequestRef.current) {
          pendingLabelRef.current = null;
        }
      }
    },
    [searchFn, resolveSelectedOption, scope],
  );

  // 🎯 Efecto para realizar búsqueda cuando cambie el query debounced
  useEffect(() => {
    if (open && debouncedSearchQuery === searchQuery && failedSearch.current !== `${scope}:${debouncedSearchQuery}`) {
      performSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, searchQuery, open, performSearch, scope]);

  // 🎯 Efecto para mantener sincronizada la opción seleccionada cuando cambia el value externo
  useEffect(() => {
    if (!value) {
      labelRequestRef.current += 1;
      pendingLabelRef.current = null;
      setSelectedOption(null);
      return;
    }

    if (selectedOption?.value === value) {
      pendingLabelRef.current = null;
      return;
    }

    const foundOption = options.find((option) => option.value === value);
    if (foundOption) {
      labelRequestRef.current += 1;
      pendingLabelRef.current = null;
      setSelectedOption(foundOption);
      return;
    }

    // Ya hay una resolución en curso para este mismo valor.
    if (pendingLabelRef.current === value || failedLabel.current === `${scope}:${value}`) return;

    pendingLabelRef.current = value;
    const requestId = ++labelRequestRef.current;
    setSelectedOption(null);
    void performInitialSearch(value, requestId);
  }, [value, selectedOption, options, performInitialSearch, scope]);

  // 🎯 Manejar selección de opción
  const handleSelect = useCallback(
    (selectedValue: string) => {
      const option = options.find((opt) => opt.value === selectedValue) || null;
      if (option) {
        setSelectedOption(option);
      }
      setValue(selectedValue);
      // 🎯 Notificar al padre la opción seleccionada (incluye label)
      onOptionSelected?.(option);
      setOpen(false);
      setSearchQuery('');
    },
    [setValue, options, onOptionSelected],
  );

  // 🎯 Manejar apertura del popover
  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      failedSearch.current = null;
      failedLabel.current = null;
      setSearchQuery('');
      setIsLoading(true);
      // Focus en el input cuando se abre
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }

    requestGenerationRef.current += 1;
    setOptions([]);
    setIsLoading(false);
    setIsLoadingMore(false);
    setSearchQuery('');
  }, []);

  // 🎯 Manejar cambio en el input de búsqueda
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // 🎯 Manejar teclas especiales
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  // Enter/espacio solo alternan desde el trigger: en la búsqueda son texto.
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      handleKeyDown(e);
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        setOpen((previous) => !previous);
      }
    },
    [handleKeyDown],
  );

  /** Altura del área scrollable: pocos resultados → encaja al contenido; muchos → tope con scroll */
  const LIST_MAX_HEIGHT_PX = 344;
  const PLACEHOLDER_AREA_PX = 96;
  const showEmptyOrLoadingPlaceholder = (isLoading && options.length === 0) || (!isLoading && options.length === 0);
  const estimatedRowsPx = options.length > 0 ? Math.max(virtualizer.getTotalSize(), options.length * 40) : 0;
  const listScrollHeight = showEmptyOrLoadingPlaceholder
    ? PLACEHOLDER_AREA_PX
    : Math.min(LIST_MAX_HEIGHT_PX, Math.max(40, estimatedRowsPx + (isLoadingMore ? 40 : 0)));

  return (
    <Popover open={open && !disabled} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            className,
            'w-full min-w-0 max-w-full',
            'bg-card-bg border border-card-border rounded px-2 py-[9px] text-sm flex justify-between items-center cursor-pointer transition-colors',
            'hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            disabled && 'pointer-events-none opacity-70',
          )}
          data-testid={dataTestId}
          role={ariaLabel ? 'combobox' : undefined}
          aria-label={ariaLabel}
          aria-expanded={open}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleTriggerKeyDown}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left', // 🎯 Truncar texto y ocupar el ancho del trigger
              selectedOption ? 'text-foreground' : ariaLabel ? 'text-complementary-text' : 'text-default-text',
            )}
          >
            {selectedOption?.label || placeholder}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="ml-2 h-2 w-2 opacity-50 text-muted-foreground flex-shrink-0"
          />
        </button>
      </PopoverTrigger>

      {/* 🎯 Mismo ancho que el trigger; modal={false} + stopPropagation en wheel evita que el scroll se pierda dentro de Dialog */}
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-w-[min(100vw-1.5rem,var(--radix-popover-trigger-width))] p-0 bg-card-bg text-popover-foreground border border-border shadow-md"
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col w-full max-h-[min(24rem,85dvh)]">
          {/* 🔍 Input de búsqueda — mismo look que Combobox (cmdk CommandInput) */}
          <div className="flex shrink-0 items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={searchQuery}
              onChange={handleSearchChange}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              onKeyDown={handleKeyDown}
            />
            {isLoading && (
              <FontAwesomeIcon icon={faSpinner} className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* 📜 Lista virtualizada con scroll infinito */}
          <div
            ref={parentRef}
            role={ariaLabel ? 'listbox' : undefined}
            aria-label={ariaLabel}
            className="min-h-0 shrink-0 overflow-y-auto overscroll-contain touch-pan-y"
            style={{ height: listScrollHeight }}
            onWheel={(e) => e.stopPropagation()}
            onScroll={(e) => {
              const target = e.target as HTMLElement;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

              // 🎯 Cargar más cuando el usuario llega al 90% del scroll
              if (scrollPercentage > 0.9 && hasMore && !isLoadingMore && !isLoading) {
                setIsLoadingMore(true);
                // La página es de la búsqueda vigente al hacer scroll; si cambia mientras
                // onLoadMore carga, sus filas y su indicador ya no le corresponden.
                const generation = requestGenerationRef.current;
                const afterPrefetch = onLoadMore ? onLoadMore() : Promise.resolve();
                afterPrefetch
                  .then(async () => {
                    if (generation !== requestGenerationRef.current) {
                      return;
                    }
                    await performSearch(debouncedSearchQuery, true);
                  })
                  .finally(() => {
                    if (generation === requestGenerationRef.current) {
                      setIsLoadingMore(false);
                    }
                  });
              }
            }}
          >
            {/* 🔄 Estado de loading (raro en filtrado local) */}
            {isLoading && options.length === 0 && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                {loadingMessage}
              </div>
            )}

            {lookupError && (
              <div role="alert" className="space-y-2 px-3 py-3 text-sm text-muted-foreground">
                <p>{t('dynamicCombobox.loadError')}</p>
                <button
                  type="button"
                  className="underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    failedSearch.current = null;
                    failedLabel.current = null;
                    void performSearch(debouncedSearchQuery, failedAppend);
                  }}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
            {/* 🕳️ Estado vacío */}
            {!isLoading && !lookupError && options.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            )}

            {/* 📋 Lista de opciones virtualizadas */}
            {options.length > 0 && (
              <>
                <div
                  style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const option = options[virtualItem.index];
                    const isSelected = option.value === value;

                    return (
                      <button
                        type="button"
                        key={virtualItem.key}
                        role={ariaLabel ? 'option' : undefined}
                        aria-selected={ariaLabel ? isSelected : undefined}
                        tabIndex={ariaLabel ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (ariaLabel && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            handleSelect(option.value);
                          }
                        }}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        className={cn(
                          'absolute top-0 left-0 w-full flex items-center px-3 py-2 text-sm cursor-pointer transition-colors',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                          isSelected && 'bg-accent text-accent-foreground',
                        )}
                        style={{
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                        onClick={() => handleSelect(option.value)}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={cn('mr-2 h-3.5 w-3.5 flex-shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                        />
                        <span className="truncate">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
                {/* 🎯 Indicador de carga para scroll infinito */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                    {loadingMoreMessage}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DynamicCombobox;
